import { useState, useEffect, useCallback } from "react";
import {
  FiInbox,
  FiUser,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiFileText,
  FiCheck,
  FiTrash2,
  FiFilter,
  FiRefreshCw,
  FiExternalLink,
  FiDownload,
  FiCalendar,
} from "react-icons/fi";
import toast from "react-hot-toast";
import "./CareersApplications.css";
import { getApplications, deleteApplication } from "../api/api";
import { extractCollection, extractPagination } from "../utils/extractCollection";

const statusConfig = {
  new: { label: "New", color: "#3b82f6", bg: "rgba(59, 130, 246, 0.15)" },
  pending: { label: "Pending", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.15)" },
  reviewed: { label: "Reviewed", color: "#8b5cf6", bg: "rgba(139, 92, 246, 0.15)" },
  accepted: { label: "Accepted", color: "#22c55e", bg: "rgba(34, 197, 94, 0.15)" },
  rejected: { label: "Rejected", color: "#ef4444", bg: "rgba(239, 68, 68, 0.15)" },
};

const defaultStats = { total: 0, new: 0, pending: 0, reviewed: 0, accepted: 0, rejected: 0 };
const PAGE_LIMIT = 10;

// ─── Pure helpers (خارج الكومبوننت) ─────────────────────────────────────────

const getName = (a) => a?.fullName || a?.name || "Unknown";
const getPosition = (a) =>
  a?.position || a?.jobTitle || a?.title || a?.department || "—";

// الـ resume ممكن يجي string أو object فيه url
const getResumeUrl = (a) =>
  typeof a?.resume === "string"
    ? a.resume
    : a?.resume?.url || a?.resume?.secure_url || "";

const sortNewestFirst = (list) =>
  [...list].sort(
    (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
  );

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const calcStats = (apps) => ({
  total: apps.length,
  new: apps.filter((r) => r.status === "new").length,
  pending: apps.filter((r) => r.status === "pending").length,
  reviewed: apps.filter((r) => r.status === "reviewed").length,
  accepted: apps.filter((r) => r.status === "accepted").length,
  rejected: apps.filter((r) => r.status === "rejected").length,
});

const previewText = (msg) =>
  msg
    ? msg.substring(0, 120) + (msg.length > 120 ? "..." : "")
    : "No message provided";

export default function CareersApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [stats, setStats] = useState(defaultStats);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // ─── Load Applications ─────────────────────────────────────────────────────

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getApplications({ page, limit: PAGE_LIMIT });
      console.log("APPLICATIONS RESPONSE:", response); // مؤقتًا للتشخيص، امسحه بعد ما تتأكد

      const apps = sortNewestFirst(extractCollection(response));

      const serverStats = response?.data?.stats ?? response?.stats;
      const pagination = extractPagination(response);

      if (serverStats) {
        setStats({ ...defaultStats, ...serverStats });
      } else {
        const local = calcStats(apps);
        // لو الباك اند بيرجع الإجمالي الحقيقي استخدمه بدل عدد الصفحة الحالية
        if (pagination?.total) local.total = pagination.total;
        setStats(local);
      }

      const tp =
        pagination?.totalPages ??
        pagination?.pages ??
        (pagination?.total ? Math.ceil(pagination.total / PAGE_LIMIT) : 1);
      setTotalPages(tp || 1);

      setApplications(apps);
    } catch (error) {
      console.error("LOAD APPLICATIONS ERROR:", error);
      toast.error(error?.data?.message || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  // ─── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (applicationId) => {
    if (!window.confirm("Are you sure you want to delete this application?")) return;

    try {
      await deleteApplication(applicationId);
      setApplications((prev) => prev.filter((r) => r._id !== applicationId));
      if (selectedApplication?._id === applicationId) setSelectedApplication(null);
      setStats((prev) => {
        const deleted = applications.find((r) => r._id === applicationId);
        if (!deleted) return prev;
        return {
          ...prev,
          total: Math.max(0, prev.total - 1),
          [deleted.status]:
            prev[deleted.status] > 0 ? prev[deleted.status] - 1 : 0,
        };
      });
      toast.success("Application deleted");
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.message || "Failed to delete application");
    }
  };

  // ─── Derived state ─────────────────────────────────────────────────────────

  const filteredApplications =
    filter === "all" ? applications : applications.filter((r) => r.status === filter);

  const selectedResumeUrl = selectedApplication ? getResumeUrl(selectedApplication) : "";

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="applications-page">
      {/* Header */}
      <div className="applications-header">
        <div className="applications-title">
          <FiInbox size={28} />
          <div>
            <h1>Job Applications</h1>
            <p>Manage applications from your website careers page</p>
          </div>
        </div>
        <button className="refresh-btn" onClick={loadApplications} disabled={loading}>
          <FiRefreshCw size={18} className={loading ? "spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="applications-stats">
        <div className="stat-card total">
          <div className="stat-icon"><FiInbox /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Applications</span>
          </div>
        </div>
        <div className="stat-card new">
          <div className="stat-icon"><FiMail /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.new}</span>
            <span className="stat-label">New</span>
          </div>
        </div>
        <div className="stat-card pending">
          <div className="stat-icon"><FiBriefcase /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
        <div className="stat-card reviewed">
          <div className="stat-icon"><FiFileText /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.reviewed}</span>
            <span className="stat-label">Reviewed</span>
          </div>
        </div>
        <div className="stat-card accepted">
          <div className="stat-icon"><FiCheck /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.accepted}</span>
            <span className="stat-label">Accepted</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="applications-filters">
        <div className="filter-group">
          <FiFilter size={16} />
          {["all", "new", "pending", "reviewed", "accepted", "rejected"].map((f) => (
            <button
              key={f}
              className={filter === f ? "active" : ""}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Applications List */}
      {loading ? (
        <div className="applications-loading">Loading applications...</div>
      ) : filteredApplications.length === 0 ? (
        <div className="applications-empty">
          <FiInbox size={48} />
          <h3>No applications found</h3>
          <p>
            When candidates submit applications through your website, they will
            appear here.
          </p>
        </div>
      ) : (
        <div className="applications-list">
          {filteredApplications.map((application) => (
            <div
              key={application._id}
              className={`application-card ${
                selectedApplication?._id === application._id ? "selected" : ""
              }`}
              onClick={() => setSelectedApplication(application)}
            >
              <div className="application-header">
                <div className="application-info">
                  <div className="application-avatar">
                    {getName(application).charAt(0).toUpperCase()}
                  </div>
                  <div className="application-details">
                    <h4>{getName(application)}</h4>
                    <span className="application-position">{getPosition(application)}</span>
                  </div>
                </div>
                <span
                  className="application-status"
                  style={{
                    color: statusConfig[application.status]?.color,
                    background: statusConfig[application.status]?.bg,
                  }}
                >
                  {statusConfig[application.status]?.label || application.status || "—"}
                </span>
              </div>

              <div className="application-preview">
                <p>{previewText(application.message)}</p>
              </div>

              <div className="application-footer">
                <div className="application-meta">
                  <span>
                    <FiMail size={14} /> {application.email}
                  </span>
                  <span>
                    <FiCalendar size={14} /> {formatDate(application.createdAt)}
                  </span>
                </div>
                <div className="application-actions">
                  <a
                    href={`mailto:${application.email}`}
                    className="action-btn"
                    title="Reply via Email"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FiExternalLink size={16} />
                    <span>Reply</span>
                  </a>
                  <button
                    className="action-btn delete"
                    title="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(application._id);
                    }}
                  >
                    <FiTrash2 size={16} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div
          className="application-modal-overlay"
          onClick={() => setSelectedApplication(null)}
        >
          <div
            className="application-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Application Details</h2>
              <button
                className="modal-close"
                onClick={() => setSelectedApplication(null)}
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="detail-section">
                <div className="detail-avatar">
                  {getName(selectedApplication).charAt(0).toUpperCase()}
                </div>
                <div className="detail-info">
                  <h3>{getName(selectedApplication)}</h3>
                  <span
                    className="detail-status"
                    style={{
                      color: statusConfig[selectedApplication.status]?.color,
                      background: statusConfig[selectedApplication.status]?.bg,
                    }}
                  >
                    {statusConfig[selectedApplication.status]?.label ||
                      selectedApplication.status ||
                      "—"}
                  </span>
                </div>
              </div>

              <div className="detail-row">
                <FiUser />
                <div>
                  <label>Position</label>
                  <span>{getPosition(selectedApplication)}</span>
                </div>
              </div>

              <div className="detail-row">
                <FiMail />
                <div>
                  <label>Email</label>
                  <a href={`mailto:${selectedApplication.email}`}>
                    {selectedApplication.email}
                  </a>
                </div>
              </div>

              {selectedApplication.phone && (
                <div className="detail-row">
                  <FiPhone />
                  <div>
                    <label>Phone</label>
                    <a href={`tel:${selectedApplication.phone}`}>
                      {selectedApplication.phone}
                    </a>
                  </div>
                </div>
              )}

              <div className="detail-row">
                <FiCalendar />
                <div>
                  <label>Submitted</label>
                  <span>{formatDate(selectedApplication.createdAt)}</span>
                </div>
              </div>

              {selectedApplication.department && (
                <div className="detail-row">
                  <FiBriefcase />
                  <div>
                    <label>Department</label>
                    <span>{selectedApplication.department}</span>
                  </div>
                </div>
              )}

              {selectedApplication.portfolio && (
                <div className="detail-row">
                  <FiExternalLink />
                  <div>
                    <label>Portfolio</label>
                    <a
                      href={selectedApplication.portfolio}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedApplication.portfolio}
                    </a>
                  </div>
                </div>
              )}

              {selectedResumeUrl && (
                <div className="detail-row">
                  <FiFileText />
                  <div>
                    <label>Resume</label>
                    <a
                      href={selectedResumeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="resume-link"
                    >
                      <FiDownload size={16} />
                      Download Resume
                    </a>
                  </div>
                </div>
              )}

              <div className="detail-message">
                <label>Cover Letter / Message</label>
                <p>{selectedApplication.message || "No message provided"}</p>
              </div>

              <div className="modal-footer">
                <a
                  href={`mailto:${selectedApplication.email}?subject=Re: Application for ${selectedApplication.position}`}
                  className="reply-btn"
                >
                  <FiMail size={18} />
                  Reply via Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
